<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import MultiSelect from 'primevue/multiselect';
import { useRouting } from '@/composables';
import { OAUTH_PROVIDER_SLUG } from '@/enum';
import { useTenantIntegrationStore } from '@/store';
import { OAuthFormIntegration } from '@/types/OAuth';

const tenantIntegrationStore = useTenantIntegrationStore();

const { loading, error, roles, users } = storeToRefs(tenantIntegrationStore);

const $routing = useRouting();

const rules = {
  enforcedRoles: {},
  bypassUsers: {},
  clientId: { required },
  clientSecret: { required },
  redirectUrl: { required },
};

const payload = ref<OAuthFormIntegration>({
  enforcedRoles: [],
  bypassUsers: [],
  clientId: '',
  clientSecret: '',
  redirectUrl: '',
});

const v$ = useVuelidate(rules, payload);

const originalForm = ref<OAuthFormIntegration | null>(null);

const tenantId = ref(Number($routing.route.params.id));

const integration = ref();

const errors = ref(null);

const isLoading = computed(() => loading.value);

onMounted(async () => await init());

const init = async () => {
  integration.value = tenantIntegrationStore.findIntegration(OAUTH_PROVIDER_SLUG.GOOGLE);

  if (integration.value) {
    const integrationRoles = integration.value.integration_role;
    const enforcedRoles = integrationRoles
      .map((role) => roles.value.find((r) => r.id === role.role_id))
      .filter(Boolean);

    const bypassUsers = integration.value.integration_bypass_users
      .map((user) => users.value.find((u) => u.id === user.user_id))
      .filter(Boolean);

    payload.value = {
      id: integration.value.id,
      enforcedRoles: enforcedRoles,
      bypassUsers: bypassUsers,
      clientId: integration.value.data.client_id,
      clientSecret: integration.value.data.client_secret,
      redirectUrl: integration.value.data.redirect_url,
    };

    originalForm.value = { ...payload.value };
  }
};

const resetDirty = () => v$.value.$reset();

const fieldStyles = (key: string, styles?: string): string => {
  const validation =
    (v$.value[key].$invalid && v$.value[key].$dirty) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const onCancel = () => {
  if (originalForm.value) {
    payload.value = { ...originalForm.value };
  }
  errors.value = null;
  v$.value.$reset();
};

const onSave = async () => {
  if (v$.value.$invalid) return;

  const formData = new FormData();
  const configData = JSON.stringify({
    client_id: payload.value.clientId,
    client_secret: payload.value.clientSecret,
    redirect_url: payload.value.redirectUrl,
  });

  formData.append('data', configData);

  payload.value.enforcedRoles.forEach((role) => formData.append('integration_roles[]', String(role.id)));
  payload.value.bypassUsers.forEach((user) => formData.append('integration_bypass_users[]', String(user.id)));

  formData.append('integration_provider_type_slug', OAUTH_PROVIDER_SLUG.GOOGLE);

  payload.value.id
    ? await tenantIntegrationStore.updateTenantIntegration(tenantId.value, payload.value.id, formData)
    : await tenantIntegrationStore.saveTenantIntegration(tenantId.value, formData);

  if (!error.value) {
    await init();
    originalForm.value = { ...payload.value };
    resetDirty();
  }
};
</script>

<template>
  <FormCard
    title="Google OAuth"
    :dirty="v$.$anyDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="v$.$anyDirty && v$.$invalid"
  >
    <template #form>
      <form id="google_oauth_form" class="w-full">
        <div class="field">
          <label for="enforced-roles">Enforced Roles</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <MultiSelect
            v-else
            id="enforced-roles"
            v-model="v$.enforcedRoles.$model"
            :options="roles"
            option-label="title"
            placeholder="Select Roles"
            display="chip"
            class="w-full"
            @blur="v$.enforcedRoles.$touch"
          />
        </div>
        <div class="field">
          <label for="bypass-users">Users that can bypass Google OAuth</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <MultiSelect
            v-else
            id="bypass-users"
            v-model="v$.bypassUsers.$model"
            data-key="id"
            :options="users"
            option-label="display_name"
            placeholder="Select Users"
            display="chip"
            :filter="true"
            :show-toggle-all="false"
            class="w-full"
            @blur="v$.bypassUsers.$touch"
          />
        </div>
        <div class="field">
          <label for="client-id" class="required">Google Client ID</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Password
            v-else
            id="client-id"
            v-model="v$.clientId.$model"
            :toggle-mask="true"
            :class="fieldStyles('clientId', 'w-full')"
            placeholder="Enter Google Client ID"
            :feedback="false"
            autocomplete="off"
          />
        </div>
        <div class="field">
          <label for="client-secret" class="required">Google Client Secret</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Password
            v-else
            id="client-secret"
            v-model="v$.clientSecret.$model"
            :toggle-mask="true"
            :class="fieldStyles('clientSecret', 'w-full')"
            placeholder="Enter Google Client Secret"
            :feedback="false"
            autocomplete="off"
          />
        </div>
        <div class="field">
          <label for="redirect-url" class="required">Google Redirect URL</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="redirect-url"
            v-model="v$.redirectUrl.$model"
            :class="fieldStyles('redirectUrl', 'w-full')"
            placeholder="Enter Google Redirect URL"
            autocomplete="off"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>
